import { isNil, isString } from 'lodash';
import { getEntityTitle, getTitleComponentId } from '@shared/data/entity';
import { getComponentLabelWithFallback, isTextComponent } from '@shared/scenes/workflows/utils';
import { API } from '@shared/types/api';

export interface EntityFieldMatch {
  label: string;
  snippet: string;
}

/**
 * Given an entity returned by the omniSearchQuery, figure out which non-title
 * field matched the keyword so we can surface it as secondary text. omniSearch
 * only matches TEXT fields server-side, so those are the only components we
 * inspect. If the title matched, or no TEXT field matched, we return null and
 * show no secondary line.
 */
export function getEntityFieldMatch(
  entity: API.WorkflowEntity,
  keyword: string,
  workflowTemplate?: API.WorkflowTemplate,
): EntityFieldMatch | null {
  const trimmed = keyword.trim();
  if (trimmed.length === 0 || isNil(workflowTemplate)) {
    return null;
  }
  const needle = trimmed.toLowerCase();

  const title = getEntityTitle(entity, workflowTemplate);
  if (isString(title) && title.toLowerCase().includes(needle)) {
    return null;
  }

  const titleComponentId = getTitleComponentId(workflowTemplate);
  for (const component of workflowTemplate.components) {
    if (component.id === titleComponentId) {
      continue;
    }
    const text = extractSearchableText(component, entity.fields?.[component.id]);
    if (isNil(text)) {
      continue;
    }
    if (text.toLowerCase().includes(needle)) {
      return {
        label: getComponentLabelWithFallback(component),
        snippet: buildSnippet(text, trimmed),
      };
    }
  }

  return null;
}

function extractSearchableText(component: API.AnyComponent, value: unknown): string | null {
  if (isNil(value) || !isTextComponent(component)) {
    return null;
  }
  return isString(value) ? value : null;
}

/**
 * Build a snippet centered on the keyword match, including up to one full word
 * on either side. Adds an ellipsis when content extends beyond that window.
 */
function buildSnippet(text: string, keyword: string): string {
  const matchStart = text.toLowerCase().indexOf(keyword.toLowerCase());
  if (matchStart === -1) {
    return text;
  }
  const matchEnd = matchStart + keyword.length;

  const start = expandLeft(text, matchStart);
  const end = expandRight(text, matchEnd);

  const prefix = start > 0 ? '…' : '';
  const suffix = end < text.length ? '…' : '';

  return `${prefix}${text.slice(start, end)}${suffix}`;
}

function isWordChar(char: string): boolean {
  return /\S/.test(char);
}

function expandLeft(text: string, index: number): number {
  let i = index;
  // skip whitespace immediately before the match
  while (i > 0 && !isWordChar(text[i - 1])) {
    i--;
  }
  // include the full preceding word
  while (i > 0 && isWordChar(text[i - 1])) {
    i--;
  }
  return i;
}

function expandRight(text: string, index: number): number {
  let i = index;
  // skip whitespace immediately after the match
  while (i < text.length && !isWordChar(text[i])) {
    i++;
  }
  // include the full following word
  while (i < text.length && isWordChar(text[i])) {
    i++;
  }
  return i;
}
